/**
 * AccountSuspendedOverlay
 *
 * Terminal full-screen overlay shown when the signed-in account has been
 * permanently suspended.
 *
 * Flutter source of truth: `AppOverlay.accountSuspendedOverlay()`
 * (`overlay_widget.dart:112-191`) — a blurred 30 %-alpha scrim (`:122-134`), a
 * title (`:141-148`), the body copy (`:149-153`) and `_contactUsButton()`
 * (`:154-179`, defined at `:193-223`).
 *
 * ⚠️ **No dismiss affordance — deliberate Flutter parity, not an omission.**
 * The Flutter overlay has no Close control and never removes itself on its own,
 * because a suspended account has nothing left to do in the app. The
 * "back/exit must stay reachable" rule does **not** apply here. Do not add a
 * close button, a click-outside handler or an Escape handler.
 *
 * **The single exit is "Contact us"** (see `onContactUs`). It navigates to the
 * public `/contact` page **without** clearing `accountSuspendedVisible`: the
 * flag stays raised for the whole suspended session and `AppOverlayHost`
 * suppresses this overlay by route instead, only while the user is on
 * `APP_OVERLAY_CONSTANTS.accountSuspendedExemptPaths` (`/contact`, `/support`,
 * `/legal` and their sub-paths — the escape hatch's full reachable closure).
 * Leaving those routes — including popping straight back here — re-blocks the
 * app. Clearing the flag instead would free the entire app the moment the user
 * navigated back, because nothing re-raises it inside a session.
 *
 * **Why Contact us and not Delete account.** The overlay used to offer a
 * "Delete account" control, which contradicted the product's own Terms: the
 * compliance copy (`l10n.deleteAccount.disclaimerParagraph2`) says a blocked or
 * suspended account may not have its data deleted, and `DeleteAccountPage.tsx:74`
 * enforces exactly that with `toast.error(l10n.deleteAccount.suspendedMessage)`.
 * The body copy already directs the user to contact support, so the single
 * control now opens the appeal path it names.
 *
 * **Fixed in both clients, not a web-only correction.** Flutter's control was
 * previously `_deleteAccountButton` with an empty `onTapUp: (details) async {}`
 * stub, so it did nothing at all; the Flutter half of this change replaced it
 * with `_contactUsButton` and a real handler that removes the root
 * `OverlayEntry`, awaits `NavigationService.navigateToContactScreen()` and
 * re-inserts on pop (`overlay_widget.dart:154-179`). The web reaches the same
 * outcome declaratively via `AppOverlayHost`'s route suppression — see that
 * file's header for the bounded differences between the two mechanisms.
 *
 * Copy reuses the web's existing suspended strings
 * (`l10n.auth.accountSuspendedTitle` / `accountSuspendedMessage`) rather than
 * minting duplicates; the control owns `l10n.appOverlay.contactButton`.
 */

import React, { useCallback } from 'react';
import {
  ExpauseButton,
  ButtonSize,
  ButtonVariant,
} from '@/presentation/components/common/controls/ExpauseButton';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { Navigator } from '@/presentation/routing/Navigator';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { AppOverlayShell } from './AppOverlayShell';

export const AccountSuspendedOverlay: React.FC = () => {
  const { l10n } = useLocale();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  /**
   * The overlay's only exit: open the public Contact page.
   *
   * It does **not** clear `accountSuspendedVisible`. `AppOverlayHost` stands
   * this overlay down only on the escape-hatch routes
   * (`APP_OVERLAY_CONSTANTS.accountSuspendedExemptPaths` — `/contact`,
   * `/support`, `/legal` and their sub-paths), so the flag stays raised and
   * every other route — including a pop straight back here — is still
   * blocked.
   */
  const onContactUs = useCallback(() => {
    Navigator.navigateToContact();
  }, []);

  return (
    <AppOverlayShell
      qaId="account-suspended-overlay"
      // Flutter scrim: `backgroundColorLight.withValues(alpha: .3)` (`:129-131`).
      scrimColor={theme.loadingOverlay}
      ariaLabel={l10n.auth.accountSuspendedTitle}
    >
      <h2
        style={{
          // Flutter `AppStyles.overlayTitleStyle` — 20 px, normal weight.
          fontSize: responsive.font['2xl'],
          fontWeight: responsive.fontWeight.regular,
          color: theme.text.primary,
          margin: 0,
        }}
      >
        {l10n.auth.accountSuspendedTitle}
      </h2>

      <p
        style={{
          // Flutter styles the body with `overlayTitleStyle` too (`:151`), but
          // the web steps it down to the body scale so the title still reads as
          // the title (styling idiom only — same controls). The body *copy*
          // deliberately differs from Flutter's literal; that divergence is
          // documented on `l10n.auth.accountSuspendedMessage`.
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.regular,
          color: theme.text.primary,
          margin: 0,
        }}
      >
        {l10n.auth.accountSuspendedMessage}
      </p>

      {/* Flutter's red control (`overlay_widget.dart:193-223`,
          `AppColors.activeRed`) maps to the shared danger variant, exactly as
          the sibling budget overlay's Close does
          (`BudgetExceededOverlay.tsx:84-92`). Only the label and the handler
          change — the destructive delete-account action is gone. */}
      <ExpauseButton
        {...qaAttr({ id: 'account-suspended-contact' })}
        variant={ButtonVariant.danger}
        size={ButtonSize.large}
        className="w-full max-w-sm"
        onClick={onContactUs}
      >
        {l10n.appOverlay.contactButton}
      </ExpauseButton>
    </AppOverlayShell>
  );
};
